/**
 * TimeEntriesTab – Time entries for a client inside ClientDetails.
 * Fetches from GET /api/time-entries/?client_id={clientId}
 * Bill Selected Time: POST /api/time-entries/generate-invoice/
 */
import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import api from '../api';
import { AuthContext } from './AuthContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatHours = (h) => {
  const n = typeof h === 'number' ? h : parseFloat(h);
  return Number.isNaN(n) ? '0.00' : n.toFixed(2);
};

const TimeEntriesTab = ({ clientId, refreshTrigger, defaultHourlyRate }) => {
  const { user } = useContext(AuthContext) || {};
  const currentUserId = user?.id ?? user?.pk ?? parseInt(localStorage.getItem('userId'), 10);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState(new Set());
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [billRate, setBillRate] = useState('');
  const [billSaving, setBillSaving] = useState(false);
  const [billError, setBillError] = useState(null);
  const [billSuccess, setBillSuccess] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    hours: '1.00',
    description: '',
    billable: true,
  });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const fetchEntries = async () => {
    if (!clientId) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/time-entries/', {
        params: { client_id: clientId },
      });
      const list = res.data?.entries ?? (Array.isArray(res.data) ? res.data : []);
      setEntries(list);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setError(msg);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [clientId, refreshTrigger]);

  const handleShowAddForm = () => {
    setShowAddForm(true);
    setAddForm({
      date: new Date().toISOString().slice(0, 10),
      hours: '1.00',
      description: '',
      billable: true,
    });
    setAddError(null);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setAddError(null);
  };

  const handleAddSubmit = async () => {
    const desc = (addForm.description || '').trim();
    if (desc.length < 3) {
      setAddError('Description must be at least 3 characters.');
      return;
    }
    let hoursVal;
    try {
      hoursVal = parseFloat(addForm.hours);
    } catch {
      setAddError('Hours must be a number.');
      return;
    }
    if (Number.isNaN(hoursVal) || hoursVal < 0.25 || hoursVal > 24) {
      setAddError('Hours must be between 0.25 and 24.');
      return;
    }

    setAddSaving(true);
    setAddError(null);
    try {
      await api.post('/api/time-entries/', {
        client_id: parseInt(clientId, 10),
        date: addForm.date,
        hours: hoursVal,
        description: desc,
        billable: addForm.billable,
      });
      setShowAddForm(false);
      fetchEntries();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setAddError(msg);
    } finally {
      setAddSaving(false);
    }
  };

  const handleStartEdit = (entry) => {
    if (entry.invoice_id) return;
    if (entry.user_id !== currentUserId) return;
    setEditingId(entry.id);
    setEditForm({
      hours: String(entry.hours),
      description: entry.description || '',
      billable: entry.billable,
    });
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const handleEditSubmit = async () => {
    if (!editingId) return;
    const desc = (editForm.description || '').trim();
    if (desc.length < 3) {
      setEditError('Description must be at least 3 characters.');
      return;
    }
    let hoursVal;
    try {
      hoursVal = parseFloat(editForm.hours);
    } catch {
      setEditError('Hours must be a number.');
      return;
    }
    if (Number.isNaN(hoursVal) || hoursVal < 0.25 || hoursVal > 24) {
      setEditError('Hours must be between 0.25 and 24.');
      return;
    }

    setEditSaving(true);
    setEditError(null);
    try {
      await api.patch(`/api/time-entries/${editingId}/`, {
        hours: hoursVal,
        description: desc,
        billable: editForm.billable,
      });
      setEditingId(null);
      fetchEntries();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setEditError(msg);
    } finally {
      setEditSaving(false);
    }
  };

  const handleBillableToggle = async (entry, checked) => {
    if (entry.invoice_id) return;
    if (entry.user_id !== currentUserId) return;
    try {
      await api.patch(`/api/time-entries/${entry.id}/`, { billable: checked });
      fetchEntries();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setError(msg);
    }
  };

  const handleDelete = async (entry) => {
    if (entry.invoice_id) return;
    if (entry.user_id !== currentUserId) return;
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await api.delete(`/api/time-entries/${entry.id}/`);
      fetchEntries();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setError(msg);
    }
  };

  const totals = entries.reduce(
    (acc, e) => {
      const h = parseFloat(e.hours) || 0;
      acc.total += h;
      if (e.billable) acc.billable += h;
      else acc.nonBillable += h;
      return acc;
    },
    { total: 0, billable: 0, nonBillable: 0 }
  );

  const canEdit = (entry) => !entry.invoice_id && String(entry.user_id) === String(currentUserId);

  const unbilledEntries = entries.filter((e) => !e.invoice_id);
  const selectedCount = selectedEntryIds.size;

  const handleToggleSelectEntry = (entryId) => {
    if (entries.find((e) => e.id === entryId)?.invoice_id) return;
    setSelectedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const handleToggleSelectAllUnbilled = () => {
    if (selectedEntryIds.size >= unbilledEntries.length) {
      setSelectedEntryIds(new Set());
    } else {
      setSelectedEntryIds(new Set(unbilledEntries.map((e) => e.id)));
    }
  };

  const handleBillSelectedClick = () => {
    setBillDialogOpen(true);
    setBillRate(defaultHourlyRate != null ? String(defaultHourlyRate) : '');
    setBillError(null);
    setBillSuccess(null);
  };

  const handleGenerateInvoice = async () => {
    const rateVal = parseFloat(billRate);
    if (Number.isNaN(rateVal) || rateVal <= 0) {
      setBillError('Rate must be a positive number.');
      return;
    }

    setBillSaving(true);
    setBillError(null);
    try {
      const res = await api.post('/api/time-entries/generate-invoice/', {
        client_id: parseInt(clientId, 10),
        entry_ids: Array.from(selectedEntryIds),
        rate: rateVal,
      });
      setBillSuccess(res.data?.invoice_number ? `Invoice ${res.data.invoice_number} created.` : 'Invoice created.');
      setSelectedEntryIds(new Set());
      await fetchEntries();
      setTimeout(() => {
        setBillDialogOpen(false);
        setBillSuccess(null);
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setBillError(msg);
    } finally {
      setBillSaving(false);
    }
  };

  // Preview: aggregate selected billable entries by user
  const billPreviewRows = (() => {
    const byUser = {};
    entries
      .filter((e) => selectedEntryIds.has(e.id) && !e.invoice_id && e.billable)
      .forEach((e) => {
        const key = e.user_id;
        if (!byUser[key]) byUser[key] = { user_name: e.user_name || '—', hours: 0 };
        byUser[key].hours += parseFloat(e.hours) || 0;
      });
    return Object.values(byUser).map((r) => ({ ...r, hours: Math.round(r.hours * 100) / 100 }));
  })();

  const billRateNum = parseFloat(billRate) || 0;
  const billTotal = billPreviewRows.reduce((sum, r) => sum + r.hours * billRateNum, 0);
  const billDateRange =
    selectedCount > 0
      ? (() => {
          const selected = entries.filter((e) => selectedEntryIds.has(e.id));
          const dates = selected.map((e) => e.date).filter(Boolean);
          if (dates.length === 0) return '';
          const minD = dates.reduce((a, b) => (a < b ? a : b));
          const maxD = dates.reduce((a, b) => (a > b ? a : b));
          return minD === maxD ? formatDate(minD) : `${formatDate(minD)} – ${formatDate(maxD)}`;
        })()
      : '';

  if (!clientId) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            No client selected.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleShowAddForm}
                disabled={showAddForm}
                sx={{ fontWeight: 600 }}
              >
                Add Time Entry
              </Button>
              {selectedCount > 0 && (
                <Button
                  variant="contained"
                  startIcon={<ReceiptIcon />}
                  onClick={handleBillSelectedClick}
                  disabled={billDialogOpen}
                  sx={{ fontWeight: 600 }}
                >
                  Bill Selected Time ({selectedCount})
                </Button>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {addError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddError(null)}>
              {addError}
            </Alert>
          )}

          {editError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}

          {showAddForm && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="date"
                  label="Date"
                  value={addForm.date}
                  onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Hours"
                  value={addForm.hours}
                  onChange={(e) => setAddForm((f) => ({ ...f, hours: e.target.value }))}
                  inputProps={{ min: 0.25, max: 24, step: 0.25 }}
                  sx={{ width: 100 }}
                />
                <TextField
                  size="small"
                  label="Description"
                  placeholder="What was done..."
                  value={addForm.description}
                  onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={addForm.billable}
                      onChange={(e) => setAddForm((f) => ({ ...f, billable: e.target.checked }))}
                    />
                  }
                  label="Billable"
                />
                <Button variant="contained" size="small" onClick={handleAddSubmit} disabled={addSaving} startIcon={addSaving ? <CircularProgress size={16} /> : <CheckIcon />}>
                  Save
                </Button>
                <Button variant="outlined" size="small" onClick={handleCancelAdd} startIcon={<CloseIcon />}>
                  Cancel
                </Button>
              </Box>
            </Paper>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : entries.length === 0 && !showAddForm ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No time entries yet. Click Add Time Entry to record work.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ fontWeight: 600 }}>
                      {unbilledEntries.length > 0 && (
                        <Checkbox
                          indeterminate={selectedCount > 0 && selectedCount < unbilledEntries.length}
                          checked={selectedCount === unbilledEntries.length && unbilledEntries.length > 0}
                          onChange={handleToggleSelectAllUnbilled}
                          aria-label="Select all unbilled"
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Staff member</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Hours</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Billable</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell padding="checkbox">
                        {!entry.invoice_id ? (
                          <Checkbox
                            checked={selectedEntryIds.has(entry.id)}
                            onChange={() => handleToggleSelectEntry(entry.id)}
                            aria-label={`Select entry ${entry.id}`}
                          />
                        ) : (
                          <Box sx={{ width: 40 }} />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(entry.date)}</TableCell>
                      <TableCell>{entry.user_name || '—'}</TableCell>
                      <TableCell align="right">
                        {editingId === entry.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editForm.hours}
                            onChange={(e) => setEditForm((f) => ({ ...f, hours: e.target.value }))}
                            inputProps={{ min: 0.25, max: 24, step: 0.25 }}
                            sx={{ width: 80 }}
                          />
                        ) : (
                          formatHours(entry.hours)
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === entry.id ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={editForm.description}
                            onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                            placeholder="Description"
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entry.description || '—'}
                            {entry.invoice_number && (
                              <Chip label={`Invoice ${entry.invoice_number}`} size="small" color="primary" variant="outlined" />
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editingId === entry.id ? (
                          <Switch
                            size="small"
                            checked={editForm.billable}
                            onChange={(e) => setEditForm((f) => ({ ...f, billable: e.target.checked }))}
                          />
                        ) : (
                          <Switch
                            size="small"
                            checked={!!entry.billable}
                            onChange={(e) => handleBillableToggle(entry, e.target.checked)}
                            disabled={!!entry.invoice_id}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingId === entry.id ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Button size="small" variant="contained" onClick={handleEditSubmit} disabled={editSaving}>
                              Save
                            </Button>
                            <Button size="small" variant="outlined" onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleStartEdit(entry)}
                              disabled={!canEdit(entry)}
                              aria-label="Edit"
                              sx={{ color: canEdit(entry) ? 'primary.main' : 'action.disabled' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(entry)}
                              disabled={!canEdit(entry)}
                              aria-label="Delete"
                              sx={{ color: canEdit(entry) ? 'error.main' : 'action.disabled' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && entries.length > 0 && (
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell />
                      <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatHours(totals.total)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Billable: {formatHours(totals.billable)} | Non-billable: {formatHours(totals.nonBillable)}
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Dialog open={billDialogOpen} onClose={() => !billSaving && setBillDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Bill Selected Time</DialogTitle>
            <DialogContent>
              {billError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBillError(null)}>
                  {billError}
                </Alert>
              )}
              {billSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {billSuccess}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Date range: {billDateRange || '—'}
            </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Hourly rate"
                value={billRate}
                onChange={(e) => setBillRate(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ mb: 2 }}
                helperText={defaultHourlyRate ? `Client default: ${defaultHourlyRate}` : 'Enter hourly rate'}
              />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Staff member</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Hours</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billPreviewRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.user_name}</TableCell>
                        <TableCell align="right">{formatHours(row.hours)}</TableCell>
                        <TableCell align="right">${(billRateNum || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">${(row.hours * (billRateNum || 0)).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Total: ${billTotal.toFixed(2)}
            </Typography>
          </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBillDialogOpen(false)} disabled={billSaving}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleGenerateInvoice} disabled={billSaving || billRateNum <= 0}>
                {billSaving ? 'Generating...' : 'Generate Invoice'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TimeEntriesTab;
